import { zodResolver } from "@hookform/resolvers/zod";
import { type GetServerSideProps } from "next";
import { signIn, useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Layout } from "~/components/Layout/Layout";
import { EventNames } from "~/mixpanel/EventNames";
import { trackEvent } from "~/mixpanel/trackEvent";
import { api } from "~/utils/api";

const registerFormData = z.object({
  name: z.string().nonempty().min(2),
  email: z.string().email(),
  password: z.string().nonempty().min(8),
  phone: z.string().nonempty().min(10),
  gender: z.string().nonempty(),
});

type registerFormData = z.infer<typeof registerFormData>;

export default function SignUp() {
  const { status } = useSession();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<registerFormData>({
    resolver: zodResolver(registerFormData),
  });

  const {
    mutateAsync: signUp,
    isLoading,
    error,
  } = api.user.register.useMutation();

  if (status === "loading")
    return <div className="loading loading-ring loading-lg"></div>;

  if (status === "authenticated") router.push("/").catch(console.error);

  return (
    <Layout
      title="Sign up"
      description="Create an account with us to receive updates, make a donation, and upgrade to a premium membership."
    >
      <form
        className="flex flex-col gap-1"
        onSubmit={handleSubmit(async (formData) => {
          await signUp(formData);
          signIn("credentials", {
            ...formData,
            redirect: true,
            callbackUrl: "/",
          }).catch(console.error);
        })}
      >
        <label htmlFor="email">Email</label>
        <input
          className="input border border-blue-500"
          type="text"
          {...register("email", { required: true })}
        />
        {errors.email && <p className="text-error">{errors.email.message}</p>}

        <label htmlFor="password">Password</label>
        <input
          className="input border border-blue-500"
          type="password"
          {...register("password", { required: true })}
        />
        {errors.password && (
          <p className="text-error">{errors.password.message}</p>
        )}

        <label htmlFor="password">Confirm Password</label>
        <input
          className="input border border-blue-500"
          type="password"
          {...register("password", { required: true })}
        />
        {errors.password && (
          <p className="text-error">{errors.password.message}</p>
        )}

        <label htmlFor="name">First and last name</label>
        <input
          className="input border border-blue-500"
          type="text"
          {...register("name", { required: true })}
        />
        {errors.name && <p className="text-error">{errors.name.message}</p>}

        <label htmlFor="phone">Phone</label>
        <input
          className="input border border-blue-500"
          type="text"
          {...register("phone", { required: true })}
        />
        {errors.phone && <p className="text-error">{errors.phone.message}</p>}

        <label htmlFor="gender">Gender</label>
        <select
          className="input border border-blue-500"
          {...register("gender", { required: true })}
        >
          <option></option>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
        {errors.gender && <p className="text-error">{errors.gender.message}</p>}

        {error && (
          <div className="alert alert-error mt-2">{error.shape?.message}</div>
        )}

        <button
          disabled={isLoading}
          className="btn-primary btn mb-2 mt-3"
          type="submit"
        >
          {isLoading ? "Registering..." : "Register"}
        </button>
        <Link className="btn-secondary btn-block btn" href="/auth/signin">
          Log in with an existing account
        </Link>
      </form>
    </Layout>
  );
}
export const getServerSideProps: GetServerSideProps = async (ctx) => {
  await trackEvent(ctx, EventNames.SIGN_UP_PAGE_VIEWED);
  return { props: {} };
};
